import { Box, FormControlLabel, RadioProps, Typography } from '@mui/material';

import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS } from '@/styles/tokens/colors';

import { StyledRadioButton } from './StyledRadioButton';

export interface RadioCardProps extends Omit<RadioProps, 'size'> {
  size?: 'small' | 'medium';
  label: React.ReactNode;
  helpText?: React.ReactNode;
  contextualHelp?: JSX.Element;
}

/** A card-shaped radio option. Use a shared name or RadioGroup for exclusive selection. */
export function RadioCard({
  label,
  helpText,
  contextualHelp,
  size = 'small',
  sx,
  ...props
}: RadioCardProps) {
  return (
    <FormControlLabel
      disabled={props.disabled}
      value={props.value}
      control={<StyledRadioButton {...props} size={size} />}
      sx={[
        {
          alignItems: 'flex-start',
          margin: 0,
          padding: 2,
          gap: 1,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          border: `1px solid ${COLORS.GRAY[300]}`,
          borderRadius: '6px',
          '&:has(.Mui-checked)': { borderColor: COLORS.GRAY[500] },
          '&:hover:not(.Mui-disabled)': { borderColor: COLORS.GRAY[800] },
          '&.Mui-disabled': { borderColor: COLORS.GRAY[300] },
          '& .MuiRadio-root': { padding: 0 },
          '& .MuiFormControlLabel-label': { minWidth: 0 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      label={
        <>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              variant={size === 'medium' ? 'body-s' : 'body-xs'}
              sx={{
                color: props.disabled ? COLORS.GRAY[300] : COLORS.TEXT.PRIMARY,
              }}
            >
              {label}
            </Typography>
            {contextualHelp && (
              <ContextualHelpTooltip>{contextualHelp}</ContextualHelpTooltip>
            )}
          </Box>
          {helpText && (
            <Typography
              component="span"
              display="block"
              variant="body-xs"
              sx={{
                mt: '2px',
                color: props.disabled ? COLORS.GRAY[300] : COLORS.GRAY[500],
              }}
            >
              {helpText}
            </Typography>
          )}
        </>
      }
    />
  );
}
